import { useEffect, useState, useRef } from "react";
import "./styles.css";
import { auth } from "./firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";

const ACCOUNTS_KEY = "friend-pass.accounts";
const CURRENT_USER_KEY = "friend-pass.currentUser";

type AccountStatus = "ok" | "ng";

type Account = {
  password: string;
  status: AccountStatus;
  hand: string[];
  selectedFriends: string[];
  following: string[];
  displayName?: string;
  bio?: string;
  age?: number;
  location?: string;
  avatar?: string; // data URL
};

const defaultFriendUsernames = ["taro", "hanako", "jun", "misaki", "shinji"];

const sampleAccounts: Record<string, Omit<Account, "hand" | "selectedFriends" | "following">> = {
  taro: { password: "sample", status: "ok" },
  hanako: { password: "sample", status: "ok" },
  jun: { password: "sample", status: "ok" },
  misaki: { password: "sample", status: "ok" },
  shinji: { password: "sample", status: "ok" },
};

const normalizeStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === "string");
};

const createSampleAccounts = (current: Record<string, any>) => {
  const next: Record<string, Account> = {};

  Object.entries(current).forEach(([username, account]) => {
    if (!account || typeof account !== "object") {
      return;
    }
    next[username] = {
      password: String(account.password ?? ""),
      status: account.status === "ng" ? "ng" : "ok",
      hand: normalizeStringArray(account.hand),
      selectedFriends: normalizeStringArray(account.selectedFriends),
      following: normalizeStringArray(account.following),
      displayName: typeof account.displayName === "string" ? account.displayName : username,
      bio: typeof account.bio === "string" ? account.bio : "",
      age: typeof account.age === "number" ? account.age : undefined,
      location: typeof account.location === "string" ? account.location : "",
      avatar: typeof account.avatar === "string" ? account.avatar : undefined,
    };
  });

  defaultFriendUsernames.forEach((username) => {
    if (!next[username]) {
      next[username] = {
        password: sampleAccounts[username]?.password ?? "sample",
        status: sampleAccounts[username]?.status ?? "ok",
        hand: [],
        selectedFriends: [],
        following: [],
        displayName: username,
        bio: "",
        age: undefined,
        location: "",
        avatar: undefined,
      };
    }
  });

  return next;
};

export default function App() {
  const [accounts, setAccounts] = useState<Record<string, Account>>({});
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const [authUsername, setAuthUsername] = useState("");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [profileSetupData, setProfileSetupData] = useState<{
    displayName: string;
    bio: string;
    age: number | "";
    location: string;
    avatar?: string;
  }>({ displayName: "", bio: "", age: "", location: "" });
  const [account, setAccount] = useState<Account | null>(null);
  const [viewMode, setViewMode] = useState<"home" | "match" | "introduce" | "notifications" | "chat" | "mypage">("home");
  type ChatMessage = {
    id: string;
    from: string;
    to: string;
    text?: string;
    timestamp: number;
    media?: string; // data URL
    mediaType?: string;
    edited?: boolean;
    deleted?: boolean;
    readBy?: string[];
  };

  const [chats, setChats] = useState<Record<string, ChatMessage[]>>({});
  const [activeChatUser, setActiveChatUser] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState("");
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editProfile, setEditProfile] = useState<{
    displayName?: string;
    bio?: string;
    age?: number | "";
    location?: string;
    avatar?: string | undefined;
  }>({});

  useEffect(() => {
    const savedAccounts = window.localStorage.getItem(ACCOUNTS_KEY);
    let loadedAccounts: Record<string, Account> = {};
    if (savedAccounts) {
      try {
        loadedAccounts = JSON.parse(savedAccounts);
      } catch {
        loadedAccounts = {};
      }
    }
    const nextAccounts = createSampleAccounts(loadedAccounts);
    setAccounts(nextAccounts);
    window.localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(nextAccounts));

    // load chats
    const savedChats = window.localStorage.getItem("friend-pass.chats");
    if (savedChats) {
      try {
        setChats(JSON.parse(savedChats));
      } catch {
        setChats({});
      }
    }

    // Firebase auth state listener
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        const username = firebaseUser.displayName ?? firebaseUser.email ?? "";
        setCurrentUser(username);
        window.localStorage.setItem(CURRENT_USER_KEY, username);
      } else {
        setCurrentUser(null);
        window.localStorage.removeItem(CURRENT_USER_KEY);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (currentUser && accounts[currentUser]) {
      setAccount(accounts[currentUser]);
    } else {
      setAccount(null);
    }
  }, [currentUser, accounts]);

  const saveAccounts = (nextAccounts: Record<string, Account>) => {
    setAccounts(nextAccounts);
    window.localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(nextAccounts));
  };

  const saveChats = (nextChats: Record<string, ChatMessage[]>) => {
    setChats(nextChats);
    window.localStorage.setItem("friend-pass.chats", JSON.stringify(nextChats));
  };

  const convId = (a: string, b: string) => [a, b].sort().join("|");

  const openConversation = (username: string) => {
    setActiveChatUser(username);
    setViewMode("chat");
  };

  const sendMessage = (to: string, text?: string, media?: string, mediaType?: string) => {
    if (!currentUser) return;
    const id = convId(currentUser, to);
    const msg: ChatMessage = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      from: currentUser,
      to,
      text: text ?? undefined,
      media: media ?? undefined,
      mediaType: mediaType ?? undefined,
      timestamp: Date.now(),
      edited: false,
      deleted: false,
      readBy: [currentUser],
    };
    const nextChats = { ...chats, [id]: [...(chats[id] ?? []), msg] };
    saveChats(nextChats);
    setChatInput("");
    // auto open conversation
    setActiveChatUser(to);
  };

  const markConversationRead = (other: string) => {
    if (!currentUser) return;
    const id = convId(currentUser, other);
    const conv = chats[id] ?? [];
    const updated = conv.map((m) => ({ ...m, readBy: Array.from(new Set([...(m.readBy ?? []), currentUser])) }));
    const next = { ...chats, [id]: updated };
    saveChats(next);
  };

  const unreadCountFor = (username: string) => {
    if (!currentUser) return 0;
    const id = convId(currentUser, username);
    const conv = chats[id] ?? [];
    return conv.filter((m) => m.from !== currentUser && !(m.readBy ?? []).includes(currentUser)).length;
  };

  const editMessage = (other: string, msgId: string, newText: string) => {
    if (!currentUser) return;
    const id = convId(currentUser, other);
    const conv = chats[id] ?? [];
    const updated = conv.map((m) => (m.id === msgId && m.from === currentUser ? { ...m, text: newText, edited: true } : m));
    const next = { ...chats, [id]: updated };
    saveChats(next);
    setEditingMessageId(null);
  };

  const deleteMessage = (other: string, msgId: string) => {
    if (!currentUser) return;
    const id = convId(currentUser, other);
    const conv = chats[id] ?? [];
    const updated = conv.map((m) => (m.id === msgId && m.from === currentUser ? { ...m, deleted: true, text: "メッセージを削除しました", media: undefined } : m));
    const next = { ...chats, [id]: updated };
    saveChats(next);
  };

  const handleLogin = async () => {
    const username = authUsername.trim();
    const email = authEmail.trim();
    if (!authPassword) {
      setAuthError("パスワードを入力してください。");
      return;
    }

    setAuthLoading(true);
    setAuthError("");

    try {
      if (authMode === "signup") {
        if (!username) {
          setAuthError("ユーザー名を入力してください。");
          setAuthLoading(false);
          return;
        }
        if (!email) {
          setAuthError("メールアドレスを入力してください。");
          setAuthLoading(false);
          return;
        }
        if (accounts[username]) {
          setAuthError("このユーザー名はすでに使用されています。");
          setAuthLoading(false);
          return;
        }
        const credential = await createUserWithEmailAndPassword(auth, email, authPassword);
        await updateProfile(credential.user, { displayName: username });

        const nextAccount: Account = {
          password: "",
          status: "ok",
          hand: defaultFriendUsernames,
          selectedFriends: [],
          following: [],
          displayName: username,
          bio: "",
          age: undefined,
          location: "",
          avatar: undefined,
        };
        saveAccounts({ ...accounts, [username]: nextAccount });
        setProfileSetupData({ displayName: username, bio: "", age: "", location: "" });
        setShowProfileSetup(true);
      } else {
        // ログイン: メールアドレスまたはユーザー名で対応
        const loginEmail = email || (username.includes("@") ? username : null);
        if (!loginEmail) {
          setAuthError("メールアドレスを入力してください。");
          setAuthLoading(false);
          return;
        }
        await signInWithEmailAndPassword(auth, loginEmail, authPassword);
      }
    } catch (err: any) {
      const code = err?.code ?? "";
      if (code === "auth/email-already-in-use") {
        setAuthError("このメールアドレスはすでに使用されています。");
      } else if (code === "auth/invalid-email") {
        setAuthError("メールアドレスの形式が正しくありません。");
      } else if (code === "auth/weak-password") {
        setAuthError("パスワードは6文字以上にしてください。");
      } else if (code === "auth/user-not-found" || code === "auth/wrong-password" || code === "auth/invalid-credential") {
        setAuthError("メールアドレスまたはパスワードが違います。");
      } else {
        setAuthError("エラーが発生しました。もう一度お試しください。");
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setAuthUsername("");
    setAuthEmail("");
    setAuthPassword("");
    setAuthError("");
  };

  const toggleFollow = (targetUsername: string) => {
    if (!account || !currentUser || targetUsername === currentUser) {
      return;
    }
    const isFollowing = account.following.includes(targetUsername);
    const next: Account = {
      ...account,
      following: isFollowing
        ? account.following.filter((username) => username !== targetUsername)
        : [...account.following, targetUsername],
    };
    updateAccount(next);
  };

  const getFollowers = (username: string) =>
    Object.entries(accounts)
      .filter(([otherUsername, otherAccount]) =>
        otherAccount.following.includes(username)
      )
      .map(([otherUsername]) => otherUsername);

  const isMutualFollow = (targetUsername: string) => {
    return (
      account?.following.includes(targetUsername) &&
      accounts[targetUsername]?.following.includes(currentUser ?? "")
    );
  };

  const updateAccount = (nextAccount: Account) => {
    if (!currentUser) {
      return;
    }
    const nextAccounts = {
      ...accounts,
      [currentUser]: nextAccount,
    };
    saveAccounts(nextAccounts);
    setAccount(nextAccount);
  };

  const saveProfileSetup = () => {
    if (!currentUser || !account) return;
    const next: Account = {
      ...account,
      displayName: profileSetupData.displayName || currentUser,
      bio: profileSetupData.bio,
      age: typeof profileSetupData.age === "number" ? profileSetupData.age : undefined,
      location: profileSetupData.location,
      avatar: profileSetupData.avatar,
    };
    updateAccount(next);
    setShowProfileSetup(false);
  };

  const handleProfileSetupAvatarFile = (file?: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setProfileSetupData((p) => ({ ...p, avatar: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const startEditProfile = () => {
    setEditProfile({
      displayName: effectiveAccount.displayName ?? effectiveCurrentUser,
      bio: effectiveAccount.bio ?? "",
      age: effectiveAccount.age ?? "",
      location: effectiveAccount.location ?? "",
      avatar: effectiveAccount.avatar ?? undefined,
    });
    setIsEditingProfile(true);
  };

  const cancelEditProfile = () => {
    setIsEditingProfile(false);
    setEditProfile({});
  };

  const saveProfile = () => {
    if (!account) return;
    const next: Account = {
      ...account,
      displayName: editProfile.displayName ?? effectiveCurrentUser,
      bio: editProfile.bio ?? "",
      age: typeof editProfile.age === "number" ? editProfile.age : undefined,
      location: editProfile.location ?? "",
      avatar: editProfile.avatar ?? undefined,
    };
    updateAccount(next);
    setIsEditingProfile(false);
  };

  const handleAvatarFile = (file?: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const data = reader.result as string;
      setEditProfile((p) => ({ ...p, avatar: data }));
    };
    reader.readAsDataURL(file);
  };

  const toggleStatus = () => {
    if (!account) {
      return;
    }
    const next: Account = {
      ...account,
      status: account.status === "ok" ? "ng" : "ok",
    };
    updateAccount(next);
  };

  const toggleFriend = (friendUsername: string) => {
    if (!account) {
      return;
    }
    const friendAccount = accounts[friendUsername];
    if (!friendAccount || friendAccount.status === "ng") {
      return;
    }
    const selectedFriends = account.selectedFriends.includes(friendUsername)
      ? account.selectedFriends.filter((username) => username !== friendUsername)
      : [...account.selectedFriends, friendUsername];

    const next: Account = {
      ...account,
      selectedFriends,
    };
    updateAccount(next);
  };

  const selectedCount = account?.selectedFriends.filter((username) => {
    if (typeof username !== "string") {
      return false;
    }
    const friend = accounts[username];
    return friend?.status === "ok";
  }).length ?? 0;

  const effectiveCurrentUser = currentUser ?? "";
  const effectiveAccount: Account = account ?? {
    password: "",
    status: "ok",
    hand: [],
    selectedFriends: [],
    following: [],
    displayName: "",
    bio: "",
    age: undefined,
    location: "",
    avatar: undefined,
  };

  const handUsernames = normalizeStringArray(effectiveAccount.hand);
  const otherUsers = Object.keys(accounts).filter((username) => username !== effectiveCurrentUser).sort();
  const followerUsernames = effectiveCurrentUser ? getFollowers(effectiveCurrentUser) : [];
  const mutualFollowUsers = otherUsers.filter(
    (username) =>
      effectiveAccount.following.includes(username) &&
      (accounts[username]?.following ?? []).includes(effectiveCurrentUser)
  );

  const followedFriendCandidates = effectiveAccount.following.flatMap((followedUsername) =>
    normalizeStringArray(accounts[followedUsername]?.hand)
  );
  const followedFriendRecommendations = Array.from(
    new Set(
      followedFriendCandidates.filter(
        (friendName) =>
          friendName !== currentUser &&
          !(account?.following ?? []).includes(friendName)
      )
    )
  );

  const storyUsers = [effectiveCurrentUser, ...otherUsers.slice(0, 4)];
  const matchCandidate = otherUsers[0] ?? null;
  const candidateCommonFriends = matchCandidate
    ? normalizeStringArray(accounts[matchCandidate]?.hand).filter((name) => effectiveAccount.hand.includes(name)).length
    : 0;
  const candidateTags = matchCandidate ? normalizeStringArray(accounts[matchCandidate]?.hand).slice(0, 3) : [];

  const renderViewContent = () => {
    if (!matchCandidate) {
      return (
        <section className="summary-card">
          <div className="summary-top">
            <span>?</span>
            <div>
              <h2>紹介候補なし</h2>
              <p>新しい候補を待っています</p>
            </div>
          </div>
          <div className="summary-bio">友達のおすすめ候補がありません。フォローしている人を増やしましょう。</div>
        </section>
      );
    }

    switch (viewMode) {
      case "match":
        return (
          <section className="match-card">
            <div className="match-card-header">
              <div className="match-avatar">{matchCandidate.slice(0, 1)}</div>
              <div>
                <strong>{matchCandidate}</strong>
                <p>{`さくらが紹介・共通の友達 ${candidateCommonFriends} 人`}</p>
              </div>
            </div>
            <div className="match-bio">趣味は映画とカフェ巡り。いま一緒に行ける友達を探しています。</div>
            <div className="match-tags">
              {candidateTags.length > 0 ? (
                candidateTags.map((tag) => <span key={tag}>{tag}</span>)
              ) : (
                <span>紹介タグなし</span>
              )}
            </div>
            <div className="match-actions">
              <button className="secondary" type="button">スキップ</button>
              <button className="primary" type="button">つながる</button>
            </div>
          </section>
        );
      case "introduce":
        return (
          <section className="recommendation-section">
            <div className="hand-header">
              <h2>紹介候補</h2>
              <p>{followedFriendRecommendations.length} 件</p>
            </div>
            {followedFriendRecommendations.length > 0 ? (
              <div className="recommendation-grid">
                {followedFriendRecommendations.map((friendName) => (
                  <div key={friendName} className="recommendation-card">
                    <div className="friend-avatar">{friendName.slice(0, 1)}</div>
                    <div>
                      <strong>{friendName}</strong>
                      <p>あなたがフォローしている人の友達です</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="subtext">フォローしている人がまだ友達を持っていないか、表示可能な友達がいません。</p>
            )}
          </section>
        );
      case "notifications":
        return (
          <section className="notifications-card">
            <h2>通知</h2>
            <p className="subtext">新しい紹介はありません。フォロー中のユーザーの更新を待ちましょう。</p>
          </section>
        );
      case "mypage":
        return (
          <section className="mypage-card">
            {isEditingProfile ? (
              <div className="profile-edit-form">
                <div className="profile-avatar-large">{(editProfile.avatar ? "" : (effectiveCurrentUser.slice(0,1).toUpperCase()))}</div>
                {editProfile.avatar && <div className="profile-avatar-preview"><img src={editProfile.avatar} alt="avatar" /></div>}
                <label>
                  表示名
                  <input value={editProfile.displayName ?? ""} onChange={(e) => setEditProfile((p) => ({ ...p, displayName: e.target.value }))} />
                </label>
                <label>
                  自己紹介
                  <textarea value={editProfile.bio ?? ""} onChange={(e) => setEditProfile((p) => ({ ...p, bio: e.target.value }))} />
                </label>
                <label>
                  年齢
                  <input type="number" value={editProfile.age ?? ""} onChange={(e) => setEditProfile((p) => ({ ...p, age: e.target.value ? Number(e.target.value) : "" }))} />
                </label>
                <label>
                  居住地
                  <input value={editProfile.location ?? ""} onChange={(e) => setEditProfile((p) => ({ ...p, location: e.target.value }))} />
                </label>
                <label>
                  アバター画像
                  <input type="file" accept="image/*" onChange={(e) => handleAvatarFile(e.target.files?.[0] ?? null)} />
                </label>
                <div className="form-actions">
                  <button className="secondary" onClick={cancelEditProfile} type="button">キャンセル</button>
                  <button className="primary" onClick={saveProfile} type="button">保存</button>
                </div>
              </div>
            ) : (
              <>
                <div className="profile-summary">
                  <div className="profile-avatar-large">{effectiveAccount.avatar ? <img src={effectiveAccount.avatar} alt="avatar" /> : effectiveCurrentUser.slice(0, 1).toUpperCase()}</div>
                  <div>
                    <h2>{effectiveAccount.displayName ?? effectiveCurrentUser}</h2>
                    {effectiveAccount.bio ? (
                      <p className="profile-bio">{effectiveAccount.bio}</p>
                    ) : (
                      <p className="profile-bio placeholder">自己紹介を追加してください（プロフィール編集から設定できます）</p>
                    )}
                  </div>
                </div>
                <div className="profile-stats">
                  <div>
                    <strong>{mutualFollowUsers.length}</strong>
                    <span>友達</span>
                  </div>
                </div>
                <button className="primary full-width" type="button" onClick={startEditProfile}>プロフィール編集</button>
              </>
            )}
          </section>
        );
      default:
        return (
          <>
            <section className="summary-card">
              <div className="summary-top">
                <span>{matchCandidate.slice(0, 1)}</span>
                <div>
                  <h2>{matchCandidate}</h2>
                  <p>{`2時間前・共通の友達 ${candidateCommonFriends} 人`}</p>
                </div>
              </div>
              <div className="summary-bio">趣味は映画とカフェ巡り。いま一緒に行ける友達を探しています。</div>
              <div className="summary-tags">
                {candidateTags.length > 0 ? (
                  candidateTags.map((tag) => <span key={tag}>{tag}</span>)
                ) : (
                  <span>紹介タグなし</span>
                )}
              </div>
            </section>
            <section className="action-card">
              <button className="secondary" type="button">スキップ</button>
              <button className="primary" type="button">つながる</button>
            </section>
          </>
        );
    }
  };

  const formatTime = (ts: number) => {
    const d = new Date(ts);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleString();
  };

  const renderChatView = () => {
    const convWith = activeChatUser;
    const id = convWith && currentUser ? convId(currentUser, convWith) : null;
    const messages = id ? chats[id] ?? [] : [];
    return (
      <section className="chat-view">
        <div className="convo-list">
          {otherUsers.map((username) => {
            const id2 = convId(currentUser ?? "", username);
            const last = (chats[id2] ?? []).slice(-1)[0];
            return (
              <button key={username} className={`convo-item ${activeChatUser === username ? "active" : ""}`} onClick={() => openConversation(username)}>
                <div className="friend-avatar">{username.slice(0, 1)}</div>
                <div>
                  <strong>{username}</strong>
                  <div className="convo-preview">{
                    last
                      ? last.text
                        ? `${last.from}: ${last.text.slice(0, 30)}`
                        : last.media
                        ? `${last.from}: [メディア]`
                        : `${last.from}`
                      : "まだ会話がありません"
                  }</div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="message-panel">
          {convWith ? (
            <>
                <div className="message-list" ref={messageListRef}>
                  {messages.map((m, idx) => (
                    <div key={m.id} className={`message ${m.from === currentUser ? "sent" : "recv"}`}>
                      {m.media && (
                        <div className="message-media">
                          <img src={m.media} alt="media" />
                        </div>
                      )}
                      {editingMessageId === m.id ? (
                        <div>
                          <input value={editingText} onChange={(e) => setEditingText(e.target.value)} />
                          <button onClick={() => editMessage(convWith, m.id, editingText)}>保存</button>
                          <button onClick={() => setEditingMessageId(null)}>キャンセル</button>
                        </div>
                      ) : (
                        <>
                          <div className="message-text">{m.text}</div>
                          <div className="message-meta">{formatTime(m.timestamp)}{m.edited ? ' ・編集済' : ''}{(m.readBy ?? []).includes(effectiveCurrentUser) ? ' ・既読' : ''}</div>
                          <div className="message-controls">
                            {m.from === currentUser && !m.deleted && (
                              <>
                                <button className="plain" onClick={() => { setEditingMessageId(m.id); setEditingText(m.text ?? ''); }}>編集</button>
                                <button className="plain" onClick={() => deleteMessage(convWith, m.id)}>削除</button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              <div className="chat-input-row">
                <input value={chatInput} onChange={(e) => setChatInput(e.target.value)} placeholder={`メッセージを送る (${convWith})`} />
                <input type="file" id="chat-file" style={{ display: 'none' }} onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file || !currentUser || !convWith) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    const data = reader.result as string;
                    sendMessage(convWith, undefined, data, file.type);
                  };
                  reader.readAsDataURL(file);
                }} />
                <label htmlFor="chat-file" className="secondary">📎</label>
                <button className="primary" onClick={() => { if (chatInput.trim()) sendMessage(convWith, chatInput.trim()); }}>送信</button>
              </div>
            </>
          ) : (
            <div className="no-convo">会話を選んでください。</div>
          )}
        </div>
      </section>
    );
  };

  // auto-scroll when messages or active conversation change
  useEffect(() => {
    const id = activeChatUser && currentUser ? convId(currentUser, activeChatUser) : null;
    const messages = id ? chats[id] ?? [] : [];
    if (messageListRef.current) {
      // scroll to bottom
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
    // mark read
    if (activeChatUser) {
      markConversationRead(activeChatUser);
    }
  }, [activeChatUser, chats]);

  if (currentUser && account && showProfileSetup) {
    return (
      <div className="App auth-page">
        <div className="auth-card profile-setup-card">
          <h1>プロフィール設定</h1>
          <p className="auth-note">あとで変更できます。スキップも可能です。</p>
          <div className="profile-setup-avatar-wrap">
            {profileSetupData.avatar ? (
              <img className="profile-setup-avatar" src={profileSetupData.avatar} alt="avatar" />
            ) : (
              <div className="profile-setup-avatar-placeholder">
                {(profileSetupData.displayName || currentUser).slice(0, 1).toUpperCase()}
              </div>
            )}
            <label className="avatar-upload-btn">
              写真を選ぶ
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => handleProfileSetupAvatarFile(e.target.files?.[0])} />
            </label>
          </div>
          <div className="auth-form">
            <label>
              表示名
              <input
                type="text"
                value={profileSetupData.displayName}
                onChange={(e) => setProfileSetupData((p) => ({ ...p, displayName: e.target.value }))}
                placeholder="あなたの名前"
              />
            </label>
            <label>
              自己紹介
              <textarea
                value={profileSetupData.bio}
                onChange={(e) => setProfileSetupData((p) => ({ ...p, bio: e.target.value }))}
                placeholder="趣味や好きなことを書いてみよう"
                rows={3}
              />
            </label>
            <label>
              年齢
              <input
                type="number"
                value={profileSetupData.age}
                onChange={(e) => setProfileSetupData((p) => ({ ...p, age: e.target.value ? Number(e.target.value) : "" }))}
                placeholder="例: 24"
              />
            </label>
            <label>
              居住地
              <input
                type="text"
                value={profileSetupData.location}
                onChange={(e) => setProfileSetupData((p) => ({ ...p, location: e.target.value }))}
                placeholder="例: 東京"
              />
            </label>
            <button className="primary submit-button" type="button" onClick={saveProfileSetup}>
              保存してはじめる
            </button>
            <button className="secondary submit-button" type="button" onClick={() => setShowProfileSetup(false)}>
              スキップ
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentUser || !account) {
    return (
      <div className="App auth-page">
        <div className="auth-card">
          <h1>{authMode === "login" ? "ログイン" : "新規アカウント"}</h1>
          <div className="auth-tabs">
            <button
              type="button"
              className={authMode === "login" ? "active" : ""}
              onClick={() => {
                setAuthMode("login");
                setAuthError("");
              }}
            >
              ログイン
            </button>
            <button
              type="button"
              className={authMode === "signup" ? "active" : ""}
              onClick={() => {
                setAuthMode("signup");
                setAuthError("");
              }}
            >
              新規登録
            </button>
          </div>
          <div className="auth-form">
            {authMode === "signup" && (
              <label>
                ユーザー名
                <input
                  type="text"
                  value={authUsername}
                  onChange={(event) => setAuthUsername(event.target.value)}
                  placeholder="表示名を入力"
                />
              </label>
            )}
            <label>
              メールアドレス
              <input
                type="email"
                value={authEmail}
                onChange={(event) => setAuthEmail(event.target.value)}
                placeholder="メールアドレスを入力"
              />
            </label>
            <label>
              パスワード
              <input
                type="password"
                value={authPassword}
                onChange={(event) => setAuthPassword(event.target.value)}
                placeholder={authMode === "signup" ? "6文字以上" : "パスワードを入力"}
              />
            </label>
            {authError && <p className="warning">{authError}</p>}
            <button className="primary submit-button" type="button" onClick={handleLogin} disabled={authLoading}>
              {authLoading ? "処理中..." : authMode === "login" ? "ログイン" : "アカウントを作成"}
            </button>
            <p className="auth-note">
              {authMode === "login"
                ? "アカウントを持っていない場合は新規登録してください。"
                : "1つのユーザー名につき1アカウントです。"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App dashboard-page mobile-shell">
      <div className="mobile-card">
        <header className="mobile-header">
          <span className="mobile-logo">Friend pass</span>
          <div className="mobile-icons">
            <button type="button">◻︎</button>
            <button type="button">◻︎</button>
          </div>
        </header>

        <section className="story-strip">
          {storyUsers.map((username) => (
            <div key={username} className={`story-item ${username === currentUser ? "active" : ""}`}>
              <div className="story-avatar">{username.slice(0, 1)}</div>
              <span>{username}</span>
            </div>
          ))}
        </section>

        {viewMode === "chat" ? renderChatView() : renderViewContent()}

        <nav className="bottom-nav">
          <button type="button" className={viewMode === "home" ? "active" : ""} onClick={() => setViewMode("home")}>ホーム</button>
          <button type="button" className={viewMode === "match" ? "active" : ""} onClick={() => setViewMode("match")}>マッチ</button>
          <button type="button" className={viewMode === "introduce" ? "active" : ""} onClick={() => setViewMode("introduce")}>紹介する</button>
          <button type="button" className={viewMode === "chat" ? "active" : ""} onClick={() => setViewMode("chat")}>チャット</button>
          <button type="button" className={viewMode === "mypage" ? "active" : ""} onClick={() => setViewMode("mypage")}>マイページ</button>
        </nav>
      </div>
    </div>
  );
}
